import {
  Fragment,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { useAppSelector } from '@app/hooks';
import {
  apiErrorStatus,
  apiErrorText,
  useImportCeoDebtsMutation,
  useParseCeoDebtImportFileMutation,
  type CeoDebtImportContract,
  type CeoDebtImportPreview,
  type CeoDebtImportResponse,
  type CeoDebtImportSheet,
  type CeoDebtInstrumentType,
} from '@shared/api';
import { focusables, setAppInert } from '@shared/lib/appInert';
import { matchesAccept } from '@shared/lib/pickFiles';
import { isAbortError } from '@shared/lib/uploadBatches';
import { AnalysisBar } from '@shared/ui/AnalysisBar';
import { Button } from '@shared/ui/Button';
import { FileDropZone } from '@shared/ui/FileDropZone';
import { IconX } from '@shared/ui/icons';
import { formatDateRu, INSTRUMENT_TYPE_LABELS } from '../ceoFormat';
import {
  ACCEPT_DEBTS_FILE,
  DEBT_TYPES,
  SHEET_LAYOUT_LABELS,
  moneyText,
  rateText,
  scheduleText,
  summarizeSchedule,
  termText,
  toImportItem,
  type ScheduleSummary,
} from './importModel';
import {
  Backdrop,
  Body,
  CellCheckbox,
  CellLabel,
  CellSelect,
  CloseButton,
  Counter,
  ErrorText,
  Footer,
  FooterGroup,
  Head,
  HeadHint,
  HeadText,
  HeadTitle,
  Lead,
  LinkButton,
  Notice,
  NoticeTitle,
  OptionHint,
  OptionLabel,
  Options,
  Panel,
  ResultHint,
  ResultList,
  ReviewTable,
  RowNote,
  SheetItem,
  SheetList,
  SheetMeta,
  SheetName,
  SheetsBox,
  StatusTag,
  Strong,
  Sub,
  Summary,
  TableScroll,
  WarnMark,
} from './importStyles';

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';
const TEMPLATE_URL = `${API_BASE}/agents/ceo/debts/import/template`;
const TEMPLATE_NAME = 'Шаблон — кредиты и лизинг.xlsx';
/** Предел файла таблицы — как у разбора на сервере. */
const MAX_DEBTS_FILE_BYTES = 15 * 1024 * 1024;
/** Колонок в таблице договоров (для строки предупреждений под договором). */
const TABLE_COLUMNS = 10;
const KNOWN_TYPES = new Set<string>(DEBT_TYPES);

/** Договор файла на просмотре. */
interface ReviewRow {
  c: CeoDebtImportContract;
  sum: ScheduleSummary;
  /** Отмечен — уйдёт в загрузку. Договор с ошибками отметить нельзя. */
  take: boolean;
  type: CeoDebtInstrumentType;
  /** Тип выбран вручную — пометка «проверьте тип» снимается. */
  typeChosen: boolean;
}

function toRows(p: CeoDebtImportPreview): ReviewRow[] {
  return p.contracts.map((c) => ({
    c,
    sum: summarizeSchedule(c, p.today),
    take: c.errors.length === 0,
    type: c.instrument_type,
    typeChosen: false,
  }));
}

/** «заголовки — строка 3 · договоров: 12 · платежей: 340». */
function sheetMeta(s: CeoDebtImportSheet): string {
  const parts: string[] = [];
  if (s.header_row != null) parts.push(`заголовки — строка ${s.header_row}`);
  if (s.layout !== 'skipped') {
    parts.push(`договоров: ${s.contracts.toLocaleString('ru-RU')}`);
    parts.push(`платежей: ${s.schedule_rows.toLocaleString('ru-RU')}`);
  }
  return parts.join(' · ');
}

/** Сохранить скачанный файл под заданным именем. */
function saveFile(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

interface DebtsImportDialogProps {
  open: boolean;
  onClose: () => void;
}

/**
 * «Загрузить из Excel» (кредиты и лизинг): файл → просмотр найденных договоров
 * и графиков → загрузка отмеченных. Разбор файла ничего не сохраняет; договор,
 * который уже есть в ОСНОВЕ (тот же номер и кредитор), обновляется.
 */
export function DebtsImportDialog({ open, onClose }: DebtsImportDialogProps) {
  // Состояние — во внутреннем компоненте: закрытие окна сбрасывает его и
  // прерывает начатый разбор файла.
  return open ? <ImportDialogBody onClose={onClose} /> : null;
}

function ImportDialogBody({ onClose }: { onClose: () => void }) {
  const accessToken = useAppSelector((s) => s.auth.accessToken);
  const [parseFile, { isLoading: reading }] = useParseCeoDebtImportFileMutation();
  const [importDebts, { isLoading: saving }] = useImportCeoDebtsMutation();

  const [fileName, setFileName] = useState('');
  const [fileError, setFileError] = useState('');
  const [preview, setPreview] = useState<CeoDebtImportPreview | null>(null);
  const [rows, setRows] = useState<ReviewRow[]>([]);
  const [pastPaid, setPastPaid] = useState(true);
  const [buildSchedule, setBuildSchedule] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [result, setResult] = useState<CeoDebtImportResponse | null>(null);
  const [templateBusy, setTemplateBusy] = useState(false);
  const [templateError, setTemplateError] = useState('');

  const alive = useRef(true);
  const pending = useRef<ReturnType<typeof parseFile> | null>(null);

  // Окно закрыли посреди разбора — запрос прерываем: ответ уже не нужен.
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      pending.current?.abort();
    };
  }, []);

  // Файл, брошенный мимо места для файла, браузер открыл бы вместо страницы —
  // и просмотр пропал бы. Пока окно открыто, бросать файлы мимо нельзя.
  useEffect(() => {
    const guard = (e: DragEvent) => {
      if (!Array.from(e.dataTransfer?.types ?? []).includes('Files')) return;
      const t = e.target;
      const el = t instanceof Element ? t : t instanceof Node ? t.parentElement : null;
      if (el?.closest('[data-debts-drop]')) return;
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'none';
    };
    window.addEventListener('dragover', guard);
    window.addEventListener('drop', guard);
    return () => {
      window.removeEventListener('dragover', guard);
      window.removeEventListener('drop', guard);
    };
  }, []);

  const step: 'file' | 'review' | 'done' = result ? 'done' : preview ? 'review' : 'file';
  const today = preview?.today ?? '';

  const stats = useMemo(() => {
    const s = { total: 0, fresh: 0, existing: 0, failed: 0, payments: 0 };
    for (const c of preview?.contracts ?? []) {
      s.total += 1;
      s.payments += c.schedule.length;
      if (c.errors.length > 0) s.failed += 1;
      else if (c.existing_id) s.existing += 1;
      else s.fresh += 1;
    }
    return s;
  }, [preview]);

  const byKey = useMemo(
    () => new Map((preview?.contracts ?? []).map((c) => [c.key, c])),
    [preview],
  );

  const eligible = rows.filter((r) => r.c.errors.length === 0);
  const taken = eligible.filter((r) => r.take);
  const allTaken = eligible.length > 0 && taken.length === eligible.length;
  const pastUnmarked = taken.reduce((n, r) => n + r.sum.pastUnmarked, 0);
  const withoutSchedule = taken.filter((r) => r.sum.count === 0).length;

  const patchRow = (key: string, p: Partial<ReviewRow>) =>
    setRows((prev) => prev.map((r) => (r.c.key === key ? { ...r, ...p } : r)));

  function setAll(take: boolean) {
    setRows((prev) => prev.map((r) => (r.c.errors.length > 0 ? r : { ...r, take })));
  }

  function requestClose() {
    if (saving) return;
    if (reading) {
      if (!window.confirm('Файл ещё читается. Прервать?')) return;
    } else if (
      step === 'review' &&
      taken.length > 0 &&
      !window.confirm('Закрыть окно? Отмеченные договоры не загружены.')
    ) {
      return;
    }
    onClose();
  }

  async function onFile(file: File) {
    if (pending.current) return;
    setFileError('');
    if (file.size === 0) {
      setFileError('Файл пустой — выберите файл с договорами.');
      return;
    }
    if (file.size > MAX_DEBTS_FILE_BYTES) {
      setFileError(
        'Файл больше 15 МБ — оставьте в нём только листы с договорами и графиками платежей.',
      );
      return;
    }
    const form = new FormData();
    form.append('file', file, file.name);
    setFileName(file.name);
    const req = parseFile(form);
    pending.current = req;
    try {
      const res = await req.unwrap();
      if (!alive.current) return;
      setPreview(res.preview);
      setRows(toRows(res.preview));
      setPastPaid(true);
      setBuildSchedule(false);
      setSaveError('');
    } catch (e) {
      if (!alive.current || isAbortError(e)) return;
      setFileError(
        apiErrorStatus(e) === 413
          ? 'Файл слишком большой — оставьте в нём только листы с договорами и графиками платежей.'
          : apiErrorText(e, 'Не удалось прочитать файл.'),
      );
    } finally {
      if (pending.current === req) pending.current = null;
    }
  }

  /** Файл не того формата место для файла отбросило бы молча — объясняем. */
  function rejectWrongType(files: File[]) {
    if (files.length === 0 || files.some((f) => matchesAccept(f.name, ACCEPT_DEBTS_FILE))) return;
    const name = files[0].name;
    setFileError(
      /\.xls$/i.test(name)
        ? `«${name}» — файл Excel 97–2003. Откройте его в Excel и сохраните как .xlsx (Файл → Сохранить как → Книга Excel).`
        : `«${name}» — нужен файл Excel (.xlsx, .xlsm) или CSV.`,
    );
  }

  async function downloadTemplate() {
    if (templateBusy) return;
    setTemplateError('');
    setTemplateBusy(true);
    try {
      const res = await fetch(TEMPLATE_URL, {
        headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
      });
      if (!res.ok) {
        let data: unknown = null;
        try {
          data = await res.json();
        } catch {
          // Не JSON (страница nginx) — причина по коду ответа.
        }
        if (alive.current) {
          setTemplateError(
            apiErrorText({ status: res.status, data }, 'Не удалось скачать шаблон.'),
          );
        }
        return;
      }
      saveFile(await res.blob(), TEMPLATE_NAME);
    } catch {
      if (alive.current) {
        setTemplateError('Не удалось скачать шаблон: нет связи с сервером. Повторите позже.');
      }
    } finally {
      if (alive.current) setTemplateBusy(false);
    }
  }

  function chooseAnotherFile() {
    if (saving) return;
    if (taken.length > 0 && !window.confirm('Убрать договоры этого файла и выбрать другой?')) {
      return;
    }
    setPreview(null);
    setRows([]);
    setFileName('');
    setFileError('');
    setSaveError('');
  }

  async function save() {
    if (saving || taken.length === 0) return;
    setSaveError('');
    try {
      const res = await importDebts({
        items: taken.map((r) => toImportItem(r.c, r.type, r.typeChosen, buildSchedule)),
        past_payments_paid: pastPaid,
      }).unwrap();
      if (alive.current) setResult(res);
    } catch (e) {
      if (!alive.current) return;
      setSaveError(
        apiErrorStatus(e) === 413
          ? 'За один раз столько договоров и платежей не загрузить — снимите отметку с части договоров и загрузите их отдельно.'
          : apiErrorText(e, 'Не удалось загрузить договоры.'),
      );
    }
  }

  const hint = preview ? `Файл «${preview.filename}»` : undefined;

  let content: ReactNode;
  let footer: ReactNode;

  if (step === 'file') {
    content = (
      <>
        <Lead>
          Подойдёт ваша рабочая таблица: столбцы программа находит по названиям. Нужны номер
          договора и кредитор (банк или лизингодатель); график платежей — по месяцам или отдельным
          листом.
        </Lead>
        <LinkButton type="button" disabled={templateBusy} onClick={downloadTemplate}>
          {templateBusy ? 'Скачиваю шаблон…' : 'Скачать шаблон'}
        </LinkButton>
        {templateError && <Notice $tone="danger">{templateError}</Notice>}
        {reading ? (
          <AnalysisBar title="Читаю файл…" hint={fileName} />
        ) : (
          <div
            data-debts-drop=""
            onDropCapture={(e) => {
              const dt = e.dataTransfer;
              // Папку место для файла разбирает само — берёт из неё первый подходящий файл.
              const dir = Array.from(dt.items ?? []).some(
                (it) => it.kind === 'file' && it.webkitGetAsEntry?.()?.isDirectory,
              );
              if (!dir) rejectWrongType(Array.from(dt.files ?? []));
            }}
            onChangeCapture={(e) => {
              const input = e.target as HTMLInputElement;
              if (input.type === 'file') rejectWrongType(Array.from(input.files ?? []));
            }}
          >
            <FileDropZone
              accept={ACCEPT_DEBTS_FILE}
              primary="Перетащите сюда файл Excel или CSV"
              hint="или нажмите, чтобы выбрать: .xlsx, .xlsm, .csv, до 15 МБ"
              onFile={onFile}
            />
          </div>
        )}
        {fileError && <Notice $tone="danger">{fileError}</Notice>}
      </>
    );
    footer = (
      <FooterGroup>
        <Button $variant="ghost" type="button" onClick={requestClose}>
          Отмена
        </Button>
      </FooterGroup>
    );
  } else if (step === 'review' && preview) {
    content = (
      <>
        <Summary>
          Найдено договоров: {stats.total.toLocaleString('ru-RU')} — новых {stats.fresh}, уже есть в
          ОСНОВЕ {stats.existing}, с ошибками {stats.failed}; платежей в графиках:{' '}
          {stats.payments.toLocaleString('ru-RU')}
        </Summary>

        {preview.sheets.length > 0 && (
          <SheetsBox>
            <summary>Листы файла: {preview.sheets.length} — что взято</summary>
            <SheetList>
              {preview.sheets.map((s, i) => {
                const meta = sheetMeta(s);
                return (
                  <SheetItem key={`${i}-${s.name}`} $skipped={s.layout === 'skipped'}>
                    <SheetName>«{s.name}»</SheetName> — {SHEET_LAYOUT_LABELS[s.layout] ?? s.layout}
                    {meta && <SheetMeta>{meta}</SheetMeta>}
                    {s.note && <SheetMeta>{s.note}</SheetMeta>}
                    {s.columns.length > 0 && (
                      <SheetMeta>Узнаны столбцы: {s.columns.join(', ')}</SheetMeta>
                    )}
                  </SheetItem>
                );
              })}
            </SheetList>
          </SheetsBox>
        )}

        {preview.warnings.length > 0 && (
          <Notice $tone="warning">
            <NoticeTitle>Обратите внимание</NoticeTitle>
            <ul>
              {preview.warnings.map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </ul>
          </Notice>
        )}

        {rows.length === 0 ? (
          <Notice $tone="warning">
            В файле не нашлось договоров. Нужны столбцы с номером договора и кредитором — сверьтесь
            с шаблоном или выберите другой файл.
          </Notice>
        ) : (
          <TableScroll>
            <ReviewTable>
              <thead>
                <tr>
                  <th className="c-check">
                    <CellCheckbox
                      type="checkbox"
                      checked={allTaken}
                      disabled={saving || eligible.length === 0}
                      aria-label="Отметить все договоры"
                      title="Отметить все договоры"
                      ref={(el) => {
                        if (el) el.indeterminate = taken.length > 0 && !allTaken;
                      }}
                      onChange={(e) => setAll(e.target.checked)}
                    />
                  </th>
                  <th>Тип</th>
                  <th>Договор</th>
                  <th>Кредитор и заёмщик</th>
                  <th className="num">Сумма договора</th>
                  <th className="num">Остаток долга</th>
                  <th>Ставка</th>
                  <th>Срок</th>
                  <th>График</th>
                  <th>Состояние</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const c = r.c;
                  const hasErrors = c.errors.length > 0;
                  const locked = hasErrors || saving;
                  const tone = hasErrors ? 'danger' : r.take ? undefined : 'muted';
                  const hasNotes = c.warnings.length > 0;
                  const unpaid = pastPaid ? r.sum.unpaidPastPaid : r.sum.unpaid;
                  const title = c.title.trim();
                  const borrower = c.borrower_entity?.trim() ?? '';
                  const asset = c.leased_asset_name?.trim() ?? '';
                  return (
                    <Fragment key={c.key}>
                      <tr data-tone={tone} data-split={hasNotes ? 'true' : undefined}>
                        <td className="c-check">
                          <CellCheckbox
                            type="checkbox"
                            checked={r.take}
                            disabled={locked}
                            aria-label={`Загрузить договор № ${c.contract_number}`}
                            title={hasErrors ? 'Договор с ошибками загрузить нельзя' : undefined}
                            onChange={(e) => patchRow(c.key, { take: e.target.checked })}
                          />
                        </td>
                        <td>
                          <CellSelect
                            value={r.type}
                            disabled={locked}
                            aria-label={`Тип договора № ${c.contract_number}`}
                            onChange={(e) =>
                              patchRow(c.key, {
                                type: e.target.value as CeoDebtInstrumentType,
                                typeChosen: true,
                              })
                            }
                          >
                            {!KNOWN_TYPES.has(r.type) && <option value={r.type}>{r.type}</option>}
                            {DEBT_TYPES.map((t) => (
                              <option key={t} value={t}>
                                {INSTRUMENT_TYPE_LABELS[t] ?? t}
                              </option>
                            ))}
                          </CellSelect>
                          {c.type_guessed && !r.typeChosen && !hasErrors && (
                            <WarnMark title="Тип определён не по столбцу «Тип» — выберите верный">
                              проверьте тип
                            </WarnMark>
                          )}
                        </td>
                        <td className="c-wide">
                          <Strong>№ {c.contract_number}</Strong>
                          {c.contract_date && <Sub>от {formatDateRu(c.contract_date)}</Sub>}
                          {title && title !== c.contract_number.trim() && <Sub>{title}</Sub>}
                          {c.status === 'closed' && <Sub>договор закрыт</Sub>}
                          {c.sources.length > 0 && <Sub>{c.sources.join('; ')}</Sub>}
                        </td>
                        <td className="c-wide">
                          <Strong>{c.creditor_name}</Strong>
                          {c.creditor_inn && <Sub>ИНН {c.creditor_inn}</Sub>}
                          {borrower && <Sub>Заёмщик: {borrower}</Sub>}
                          {asset && (
                            <Sub>
                              Предмет лизинга: {asset}
                              {c.leased_asset_count ? ` (${c.leased_asset_count} ед.)` : ''}
                            </Sub>
                          )}
                        </td>
                        <td className="num">
                          {c.total_limit == null ? '—' : moneyText(c.total_limit, c.currency)}
                        </td>
                        <td className="num">
                          {c.principal_balance != null ? (
                            moneyText(c.principal_balance, c.currency)
                          ) : r.sum.count > 0 ? (
                            <span title="Сумма неоплаченных платежей по графику">
                              <CellLabel>по графику:</CellLabel>
                              {moneyText(unpaid, c.currency)}
                            </span>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="nowrap">
                          {rateText(c)}
                          {c.rate_type === 'subsidized' && <Sub>льготная</Sub>}
                        </td>
                        <td>{termText(c.start_date, c.end_date)}</td>
                        <td>{scheduleText(r.sum)}</td>
                        <td className="c-status">
                          {hasErrors ? (
                            <ErrorText>
                              <strong>Ошибка:</strong> {c.errors.join('; ')}
                            </ErrorText>
                          ) : c.existing_id ? (
                            <>
                              <StatusTag $tone="info">Уже есть — обновится</StatusTag>
                              {c.existing_title && <Sub>в ОСНОВЕ: {c.existing_title}</Sub>}
                            </>
                          ) : (
                            <StatusTag $tone="success">Новый</StatusTag>
                          )}
                        </td>
                      </tr>
                      {hasNotes && (
                        <tr className="notes" data-tone={tone}>
                          <td />
                          <td colSpan={TABLE_COLUMNS - 1}>
                            {c.warnings.map((w, i) => (
                              <RowNote key={i}>{w}</RowNote>
                            ))}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </ReviewTable>
          </TableScroll>
        )}

        {(pastUnmarked > 0 || withoutSchedule > 0) && (
          <Options>
            {pastUnmarked > 0 && (
              <OptionLabel>
                <input
                  type="checkbox"
                  checked={pastPaid}
                  disabled={saving}
                  onChange={(e) => setPastPaid(e.target.checked)}
                />
                <span>
                  Платежи с датой раньше сегодняшней, по которым в таблице нет отметки об оплате,
                  считать оплаченными{' '}
                  <OptionHint>
                    (таких платежей: {pastUnmarked.toLocaleString('ru-RU')}, сегодня{' '}
                    {formatDateRu(today)})
                  </OptionHint>
                </span>
              </OptionLabel>
            )}
            {withoutSchedule > 0 && (
              <OptionLabel>
                <input
                  type="checkbox"
                  checked={buildSchedule}
                  disabled={saving}
                  onChange={(e) => setBuildSchedule(e.target.checked)}
                />
                <span>
                  Где в таблице нет графика — рассчитать график по ставке и сроку{' '}
                  <OptionHint>(договоров без графика: {withoutSchedule})</OptionHint>
                </span>
              </OptionLabel>
            )}
          </Options>
        )}

        {saveError && <Notice $tone="danger">{saveError}</Notice>}
      </>
    );
    footer = (
      <>
        <FooterGroup>
          <Button $variant="ghost" type="button" disabled={saving} onClick={requestClose}>
            Отмена
          </Button>
          <Button $variant="secondary" type="button" disabled={saving} onClick={chooseAnotherFile}>
            Выбрать другой файл
          </Button>
        </FooterGroup>
        <FooterGroup>
          {eligible.length > 0 && (
            <Counter>
              Отмечено: {taken.length} из {eligible.length}
            </Counter>
          )}
          <Button type="button" disabled={saving || taken.length === 0} onClick={save}>
            {saving ? 'Загружаю…' : `Загрузить (${taken.length})`}
          </Button>
        </FooterGroup>
      </>
    );
  } else if (result) {
    const failed = result.results.filter((x) => x.status === 'error');
    content = (
      <>
        <Notice $tone={result.failed > 0 ? 'warning' : 'success'}>
          <NoticeTitle>
            Загружено: {result.created}, обновлено: {result.updated}, ошибок: {result.failed}
          </NoticeTitle>
        </Notice>
        {failed.length > 0 && (
          <div>
            <Strong>Не загружены:</Strong>
            <ResultList>
              {failed.map((x) => {
                const c = byKey.get(x.key);
                return (
                  <li key={x.key}>
                    {c ? `№ ${c.contract_number} — ${c.creditor_name}` : x.key}:{' '}
                    {x.message?.trim() || 'причина не указана'}
                  </li>
                );
              })}
            </ResultList>
            <ResultHint>
              Исправьте эти договоры в файле и загрузите его ещё раз: договоры, которые уже есть в
              ОСНОВЕ, не задвоятся, а обновятся.
            </ResultHint>
          </div>
        )}
      </>
    );
    footer = (
      <>
        <FooterGroup />
        <FooterGroup>
          <Button type="button" onClick={onClose}>
            Готово
          </Button>
        </FooterGroup>
      </>
    );
  }

  return (
    <DialogFrame
      title="Кредиты и лизинг из Excel"
      hint={hint}
      wide={step === 'review'}
      focusKey={step}
      closeDisabled={saving}
      onRequestClose={requestClose}
      footer={footer}
    >
      {content}
    </DialogFrame>
  );
}

interface DialogFrameProps {
  title: string;
  hint?: string;
  /** Широкое окно — под таблицу договоров. */
  wide: boolean;
  /** Сменился шаг — фокус возвращается в окно (кнопка прежнего шага исчезла). */
  focusKey: string;
  closeDisabled: boolean;
  onRequestClose: () => void;
  footer: ReactNode;
  children: ReactNode;
}

/**
 * Окно поверх страницы: шире общего `Dialog` — под таблицу договоров. Как у
 * него: фокус в окне и не уходит из него по Tab, Esc и щелчок по подложке —
 * «закрыть», остальное приложение недоступно, после закрытия фокус
 * возвращается на кнопку, которой окно открыли.
 */
function DialogFrame({
  title,
  hint,
  wide,
  focusKey,
  closeDisabled,
  onRequestClose,
  footer,
  children,
}: DialogFrameProps) {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onRequestClose);

  useEffect(() => {
    closeRef.current = onRequestClose;
  }, [onRequestClose]);

  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setAppInert(true);
    return () => {
      setAppInert(false);
      if (opener?.isConnected) opener.focus({ preventScroll: true });
    };
  }, []);

  useEffect(() => {
    const panel = panelRef.current;
    if (panel && !panel.contains(document.activeElement)) panel.focus({ preventScroll: true });
  }, [focusKey]);

  // Esc — с окна, а не только с его элементов: фокус мог остаться на странице.
  useEffect(() => {
    const onEsc = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') closeRef.current();
    };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, []);

  function trapTab(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key !== 'Tab') return;
    const panel = panelRef.current;
    if (!panel) return;
    const items = focusables(panel);
    const first = items[0];
    const last = items[items.length - 1];
    if (!first || !last) {
      e.preventDefault();
      panel.focus();
      return;
    }
    const active = document.activeElement;
    if (e.shiftKey && (active === first || active === panel)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  }

  return createPortal(
    <Backdrop
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) closeRef.current();
      }}
    >
      <Panel
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        $wide={wide}
        onKeyDown={trapTab}
      >
        <Head>
          <HeadText>
            <HeadTitle id={titleId}>{title}</HeadTitle>
            {hint && <HeadHint>{hint}</HeadHint>}
          </HeadText>
          <CloseButton
            type="button"
            disabled={closeDisabled}
            onClick={() => closeRef.current()}
            aria-label="Закрыть"
            title="Закрыть (Esc)"
          >
            <IconX size={18} />
          </CloseButton>
        </Head>
        <Body>{children}</Body>
        <Footer>{footer}</Footer>
      </Panel>
    </Backdrop>,
    document.body,
  );
}
